<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.showCoupon") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #E1423D">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper" v-if="!loading">
      <form>
        <div class="row">
          <!-- Start:: Name Input -->
          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.couponName')"
            v-model.trim="data.name"
            disabled
          />
          <!-- End:: Name Input -->

          <!-- Start:: Code Input -->
          <base-input
            col="12"
            type="text"
            :placeholder="$t('PLACEHOLDERS.usageCode')"
            v-model.trim="data.code"
            disabled
          />
          <!-- End:: Code Input -->

          <!-- Start:: Discount Percentage Input -->
          <div class="col-12 my-5">
            <div class="input_wrapper top_label">
              <label class="form-label">
                {{ $t('PLACEHOLDERS.discountPercentage') }}
              </label>
              <div style="display: flex; align-items: center;">
                <input
                  class="form-control"
                  type="number"
                  v-model.number="data.discount_percentage"
                  disabled
                  style="flex: 1;"
                />
                <span style="color: #E1423D; font-weight: 600; margin-right: 10px; font-size: 1.1rem;">%</span>
              </div>
            </div>
          </div>
          <!-- End:: Discount Percentage Input -->

          <!-- Start:: Start Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :placeholder="$t('PLACEHOLDERS.startDate')"
            v-model.trim="data.starts_at"
            disabled
          />
          <!-- End:: Start Date Input -->

          <!-- Start:: End Date Input -->
          <base-picker-input
            col="6"
            type="date"
            :placeholder="$t('PLACEHOLDERS.endDate')"
            v-model.trim="data.ends_at"
            disabled
          />
          <!-- End:: End Date Input -->

          <!-- Start:: Usage Count -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.usageCount')"
            v-model="data.usage_count"
            disabled
          />
          <!-- End:: Usage Count -->

          <!-- Start:: Expiration Status -->
          <div class="col-6 my-5">
            <div class="input_wrapper top_label">
              <label class="form-label">
                {{ $t('PLACEHOLDERS.expirationStatus') }}
              </label>
              <input
                class="form-control"
                type="text"
                :value="expirationStatusText"
                disabled
              />
            </div>
          </div>
          <!-- End:: Expiration Status -->

          <!-- Start:: Status Input -->
          <base-select-input
            col="6"
            :optionsList="statusOptions"
            :placeholder="$t('PLACEHOLDERS.status')"
            v-model="data.is_active"
            disabled
          />
          <!-- End:: Status Input -->

          <!-- Start:: Users Multiselect Input -->
          <base-select-input
            col="6"
            :optionsList="usersList"
            :placeholder="$t('PLACEHOLDERS.users')"
            v-model="data.user_ids"
            multiple
            disabled
          />
          <!-- End:: Users Multiselect Input -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->

    <!-- Start:: Loading State -->
    <div v-if="loading" class="text-center py-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <!-- End:: Loading State -->
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "ShowCoupon",

  data() {
    return {
      // Start:: Loader Control Data
      loading: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        code: null,
        discount_percentage: null,
        starts_at: null,
        ends_at: null,
        usage_count: 0,
        is_active: null,
        user_ids: [],
      },
      // End:: Data Collection To Send

      usersList: [],
    };
  },

  computed: {
    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.active"),
          value: true,
        },
        {
          id: 2,
          name: this.$t("STATUS.notActive"),
          value: false,
        },
      ];
    },

    expirationStatusText() {
      if (!this.data.starts_at || !this.data.ends_at) return "-";
      
      const now = moment();
      const startDate = moment(this.data.starts_at);
      const endDate = moment(this.data.ends_at);

      if (now.isBefore(startDate)) {
        return this.$t("STATUS.notStartedYet");
      } else if (now.isAfter(endDate)) {
        return this.$t("STATUS.expired");
      } else {
        return this.$t("STATUS.active");
      }
    },
  },

  methods: {
    // Start:: Get Coupon Data
    async getCouponData() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: `coupons/${this.$route.params.id}`,
        });
        const coupon = res.data.data;
        
        // Set form data
        this.data.name = coupon.name;
        this.data.code = coupon.code;
        this.data.discount_percentage = coupon.discount_percentage;
        this.data.starts_at = coupon.starts_at
          ? moment(coupon.starts_at).format("YYYY-MM-DD")
          : null;
        this.data.ends_at = coupon.ends_at
          ? moment(coupon.ends_at).format("YYYY-MM-DD")
          : null;
        this.data.usage_count = coupon.usage_count || 0;

        // Set status
        this.data.is_active = {
          id: coupon.is_active ? 1 : 2,
          name: coupon.is_active
            ? this.$t("STATUS.active")
            : this.$t("STATUS.notActive"),
          value: coupon.is_active,
        };

        // Set users if available
        if (coupon.users && Array.isArray(coupon.users)) {
          this.data.user_ids = coupon.users.map((user) => ({
            id: typeof user === 'object' ? user.id : user,
            name: typeof user === 'object' ? (user.name || user.fullname || `User ${user.id}`) : `User ${user}`,
          }));
        }
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.loading = false;
      }
    },
    // End:: Get Coupon Data

    // Start:: Get Users
    async getUsers() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "clients?page=0&limit=0&is_active=1",
        });
        this.usersList = (res.data.data?.data || res.data.data || []).map(
          (user) => ({
            id: user.id,
            name: user.name || user.fullname || `${user.first_name || ''} ${user.last_name || ''}`.trim() || `User ${user.id}`,
          })
        );
      } catch (error) {
        console.log(error.response?.data?.message);
        this.usersList = [];
      }
    },
    // End:: Get Users
  },

  created() {
    // Start:: Fire Methods
    this.getUsers();
    this.getCouponData();
    // End:: Fire Methods
  },
};
</script>
